import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "confirmButton", "checkbox", "modal"];
  static values = {
    confirmText: { type: String, default: "" },
    caseSensitive: { type: Boolean, default: false },
    modalTitle: { type: String, default: "Confirm Action" },
    modalText: { type: String, default: "Are you sure you want to proceed?" },
    confirmButtonText: { type: String, default: "Confirm" },
    cancelButtonText: { type: String, default: "Cancel" },
    useModal: { type: Boolean, default: false },
    originalPath: { type: String, default: "" },
    turboMethod: { type: String, default: "" },
    modalId: { type: String, default: "" },
    extraFormParams: { type: String, default: "{}" },
    turbo: { type: Boolean, default: true },
    // When set, confirming submits this existing form (by DOM id) instead of
    // building a request from originalPath — for bulk actions whose payload
    // (e.g. checked checkboxes) lives in a form the dialog can't contain.
    formId: { type: String, default: "" }
  };

  connect() {
    this.confirmed = false;
    this.confirmTextArray = this.parseConfirmText();

    if (this.confirmTextArray.length === 0 && !this.hasInputTarget && !this.hasCheckboxTarget) {
      this.confirmed = true;
    }
    
    // Only set up form handling if we have the necessary targets
    if (this.hasInputTarget || this.hasConfirmButtonTarget) {
      this.updateConfirmButton();

      // Add form submission prevention
      const form = this.element.closest("form");
      if (form) {
        this.boundHandleSubmit = this.handleSubmit.bind(this);
        form.addEventListener("submit", this.boundHandleSubmit);
      }

      // Add input listeners
      if (this.hasInputTarget) {
        this.inputTarget.addEventListener("input", () => this.checkConfirmation());
        this.inputTarget.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            if (this.confirmed) {
              event.preventDefault();
              this.confirm();
            } else {
              // Prevent form submission when confirmation is not valid
              event.preventDefault();
            }
          }
        });
      }

      // Add checkbox listeners
      if (this.hasCheckboxTarget) {
        this.checkboxTargets.forEach((checkbox) => {
          checkbox.addEventListener("change", () => this.checkConfirmation());
        });
      }
    }
  }

  disconnect() {
    // Remove form submission listener
    const form = this.element.closest("form");
    if (form && this.boundHandleSubmit) {
      form.removeEventListener("submit", this.boundHandleSubmit);
    }

    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("input", () => this.checkConfirmation());
    }

    if (this.hasCheckboxTarget) {
      this.checkboxTargets.forEach((checkbox) => {
        checkbox.removeEventListener("change", () => this.checkConfirmation());
      });
    }
  }

  parseConfirmText() {
    if (!this.confirmTextValue) return [];

    // Simple comma-separated approach for arrays
    if (this.confirmTextValue.includes(",")) {
      return this.confirmTextValue
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [this.confirmTextValue];
  }

  checkConfirmation() {
    // Check text confirmation
    let textConfirmed = false;
    if (this.hasInputTarget && this.confirmTextArray.length > 0) {
      const inputValue = this.caseSensitiveValue ? this.inputTarget.value : this.inputTarget.value.toLowerCase();

      if (this.confirmTextArray.length === 1) {
        const confirmValue = this.caseSensitiveValue
          ? this.confirmTextArray[0]
          : this.confirmTextArray[0].toLowerCase();
        textConfirmed = inputValue === confirmValue;
      } else {
        textConfirmed = this.confirmTextArray.some((text) => {
          const confirmValue = this.caseSensitiveValue ? text : text.toLowerCase();
          return inputValue === confirmValue;
        });
      }
    } else {
      textConfirmed = true;
    }

    // Check checkbox confirmation
    let checkboxConfirmed = true;
    if (this.hasCheckboxTarget && this.checkboxTargets.length > 0) {
      checkboxConfirmed = this.checkboxTargets.every((checkbox) => checkbox.checked);
    }

    this.confirmed = textConfirmed && checkboxConfirmed;
    this.updateConfirmButton();
  }

  updateConfirmButton() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = !this.confirmed;

      if (this.confirmed) {
        this.confirmButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
      } else {
        this.confirmButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
      }
    }
  }

  confirm() {
    if (!this.confirmed) return false;

    const beforeConfirmEvent = new CustomEvent("confirmation:before", {
      detail: {
        value: this.getConfirmationValue(),
        controller: this,
      },
      cancelable: true,
    });

    if (!this.element.dispatchEvent(beforeConfirmEvent)) {
      return false;
    }

    this.performConfirmation();

    this.element.dispatchEvent(
      new CustomEvent("confirmation:confirmed", {
        detail: {
          value: this.getConfirmationValue(),
          controller: this,
        },
      })
    );

    return true;
  }

  handleSubmit(event) {
    // Only allow form submission if confirmation is valid
    if (!this.confirmed) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  cancel() {
    this.reset();
    
    // Close modal if it's open
    if (this.useModalValue) {
      const modal = this.element.closest('dialog') || this.element;
      if (modal && modal.close) {
        modal.close();
        document.body.style.overflow = "";
      }
    }
    
    this.element.dispatchEvent(
      new CustomEvent("confirmation:cancelled", {
        detail: { controller: this },
      })
    );
  }

  reset() {
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }

    if (this.hasCheckboxTarget) {
      this.checkboxTargets.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }

    if (this.confirmTextArray.length === 0 && !this.hasInputTarget && !this.hasCheckboxTarget) {
      this.confirmed = true;
    } else {
      this.confirmed = false;
    }

    this.updateConfirmButton();
  }

  performConfirmation() {
    if (this.useModalValue) {
      // Close modal first
      const modal = this.element.closest('dialog') || this.element;
      if (modal && modal.close) {
        modal.close();
        document.body.style.overflow = "";
      }
    }

    // Submit an existing form by id (confirmation-gated bulk actions).
    if (this.formIdValue) {
      document.getElementById(this.formIdValue)?.requestSubmit();
      return;
    }

    // Execute the original action
    if (this.originalPathValue) {
      if (this.turboMethodValue) {
        // Create a form to submit the request with the specified method
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.originalPathValue;
        form.style.display = 'none';
        form.setAttribute('data-turbo', this.turboValue ? 'true' : 'false');
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        if (csrfToken) {
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'authenticity_token';
          csrfInput.value = csrfToken.content;
          form.appendChild(csrfInput);
        }
        
        // Add method override
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = this.turboMethodValue.toUpperCase();
        form.appendChild(methodInput);
        
        // Add extra form params (e.g. member[avatar_purge]=1)
        try {
          const extra = typeof this.extraFormParamsValue === 'string' ? JSON.parse(this.extraFormParamsValue || '{}') : (this.extraFormParamsValue || {});
          for (const [name, value] of Object.entries(extra)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          }
        } catch (e) {
          // ignore invalid JSON
        }

        // Collect checked checkboxes from the parent DataList (bulk actions).
        // Select-all-across-pages (TASK-256, mirrors checkbox-select-all's
        // own injectSelection for an UNCONFIRMED button): once "Select all N
        // that <filter>" is active the thead/page checkboxes no longer
        // stand for the whole scope, so a single select_scope=filter field
        // goes instead of whatever happens to be ticked on this page.
        const dataListContainer = this.element.closest('[data-controller*="checkbox-select-all"]');
        if (dataListContainer) {
          const scopeInput = dataListContainer.querySelector('input[data-checkbox-select-all-target="scopeInput"]');
          if (scopeInput && scopeInput.value === "filter") {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'select_scope';
            input.value = 'filter';
            form.appendChild(input);
          } else {
            dataListContainer.querySelectorAll('input[type="checkbox"][data-checkbox-select-all-target="checkbox"]:checked').forEach((cb) => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = cb.name;
              input.value = cb.value;
              form.appendChild(input);
            });
          }
        }

        document.body.appendChild(form);
        form.requestSubmit();
      } else {
        // Regular navigation
        window.location.href = this.originalPathValue;
      }
    } else {
      // Fallback to form submission
      const form = this.element.closest("form");
      if (form) {
        form.submit();
      }
    }
  }

  getConfirmationValue() {
    const textValue = this.hasInputTarget ? this.inputTarget.value : "";
    const checkboxValues = this.hasCheckboxTarget
      ? this.checkboxTargets.map((checkbox) => ({
          id: checkbox.id,
          checked: checkbox.checked,
        }))
      : [];

    return {
      text: textValue,
      checkboxes: checkboxValues,
    };
  }

  // Method to open confirmation modal (called from LinkButton)
  openConfirmationModal(event) {
    event.preventDefault();
    
    if (this.useModalValue) {
      // Find the modal in the document - use modal_id if available, otherwise find any modal
      let modal;
      if (this.modalIdValue) {
        modal = document.getElementById(this.modalIdValue);
      } else {
        modal = document.querySelector(`dialog[data-confirmation-target="modal"]`);
      }
      
      if (modal) {
        modal.showModal();
        document.body.style.overflow = "hidden";
        
        // Focus on input if available
        const input = modal.querySelector('[data-confirmation-target="input"]');
        if (input) {
          setTimeout(() => input.focus(), 100);
        }
      }
    }
  }

  // Handle backdrop click (clicking outside modal content)
  backdropClose(event) {
    // Only close if clicking directly on the dialog (backdrop), not its children
    if (event.target === this.element) {
      this.cancel();
    }
  }
}
